import {Controller} from "@hotwired/stimulus"
import {SlickRowSelectionModel, SlickDataView, SlickGrid, SlickResizer, SlickCellSelectionModel, SlickCellCopyManager} from "slickgrid"
import {Type, Calendar1, Hash, FileText, Layers, Database, Filter, createIcons} from 'lucide';

export default class extends Controller {
  static targets = ['element', "create", "update", "delete", "hitems", "updateCells", "removeCells"];

  static values = {
    headers: {type: Array, default: []},
    url: {type: String, default: undefined},
    skey: {type: String, default: ''},
    rowModel: {type: String, default: 'infinite'},
    totalCount: Number,
    rowHeight: Number,
    headerHeight: Number,
    originalTotalCount: Number,
    defaultFilterModel: Object,
    fitToTable: {type: Boolean, default: false},
    fromCustomApp: {type: Boolean, default: false},
    rows: {type: Array, default: []},
    filters: {type: Array, default: []},
    context: {type: Object, default: {}},
    showLink: {type: Boolean, default: true},
    showFilters: {type: Boolean, default: true},
    filterUrl: {type: String, default: ''},
    rowIdField: {type: String, default: 'laz_id'}
  }

  connect() {
    this.hiddenColumns = new Set(this.loadHiddenColumns());
    this.initGrid();
  }

  getStorageKey() {
    return `data-rows-cols:${this.urlValue || this.element.id}`;
  }

  loadHiddenColumns() {
    try { return JSON.parse(localStorage.getItem(this.getStorageKey())) || []; } catch { return []; }
  }

  saveHiddenColumns() {
    localStorage.setItem(this.getStorageKey(), JSON.stringify([...this.hiddenColumns]));
  }

  initGrid() {
    this.elementTarget.innerHTML = '';

    // AutoHeight enabled: container should adapt to content
    this.elementTarget.style.width = '100%';

    let options = {
      enableColumnReorder: false,
      forceFitColumns: true,
      autoHeight: false,
      enableTextSelectionOnCells: true,
      enableCellNavigation: true,
      selectedCellCssClass: this.element.classList.contains('lz-report-worksheet') ? 'lz-report-cell-selected' : 'selected',
      explicitInitialization: true,
      rowHeight: 35,
      frozenColumn: 0
    };

    this.dataView = new SlickDataView();

    this.dataView.onRowCountChanged.subscribe((e, args) => {
      this.grid.updateRowCount();
      this.grid.render();
    });

    this.dataView.onRowsChanged.subscribe((e, args) => {
      this.grid.invalidateRows(args.rows);
      this.grid.render();
    });

    this.grid = new SlickGrid(this.elementTarget, this.dataView, this.getColumns(), options);

    // SlickGrid's handleClick calls getTextSelection()+setTextSelection() to preserve
    // text selection for Firefox. When a text selection exists, setTextSelection() calls
    // window.getSelection().addRange() which fires a selectionchange event. CodeMirror's
    // global selectionchange listener then crashes when view.root is unavailable.
    // Returning null here skips the addRange() call entirely.
    this.grid.getTextSelection = () => null

    this.grid.setSelectionModel(new SlickCellSelectionModel());

    // Register copy manager to enable Ctrl+C / Cmd+C clipboard copy
    const copyManager = new SlickCellCopyManager();
    this.grid.registerPlugin(copyManager);

    copyManager.onCopyCells.subscribe((e, args) => {
      const ranges = args.ranges;
      if (!ranges || ranges.length === 0) return;

      const rows = [];
      for (let i = 0; i < ranges.length; i++) {
        const range = ranges[i];
        for (let r = range.fromRow; r <= range.toRow; r++) {
          const cellValues = [];
          for (let c = range.fromCell; c <= range.toCell; c++) {
            const cellNode = this.grid.getCellNode(r, c);
            const val = cellNode ? cellNode.textContent.trim() : '';
            cellValues.push(val);
          }
          rows.push(cellValues.join('\t'));
        }
      }
      const text = rows.join('\n');
      navigator.clipboard.writeText(text).catch(err => console.error('Copy failed:', err));

      // Blink copied cells with indigo color animation
      const copiedCells = this.elementTarget.querySelectorAll('.slick-cell.copied');
      copiedCells.forEach(cell => {
        cell.classList.add('slick-cell-copy-flash');
        setTimeout(() => cell.classList.remove('slick-cell-copy-flash'), 600);
      });
    });

    this.grid.onActiveCellChanged.subscribe((e, args) => {
      const activeCellNode = this.grid.getCellNode(args.row, args.cell);
      if (activeCellNode) {
        const cellRect = activeCellNode.getBoundingClientRect();
        const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
        if (cellRect.top < 20) {
          window.scrollBy({top: cellRect.top - 20, behavior: 'smooth'});
        } else if (cellRect.bottom > viewportHeight - 20) {
          window.scrollBy({top: cellRect.bottom - (viewportHeight - 20), behavior: 'smooth'});
        }
      }
    });

    this.dataView.beginUpdate();
    this.dataView.setItems(this.rowsValue, this.rowIdFieldValue);
    this.dataView.endUpdate();

    this.resizer = new SlickResizer({
      container: '.slick-main-container',
      minHeight: 200,
      minWidth: 100
    });
    this.grid.registerPlugin(this.resizer);

    this.grid.init();
    this.resizeGrid();

    this.renderHeaderIcons();

    this.grid.onSort.subscribe((e, args) => {
      if (e.target && e.target.closest('a')) {
        return;
      }

      const field = args.sortCol.field;
      this.dataView.sort((row1, row2) => {
        let val1 = row1[field];
        let val2 = row2[field];
        if (val1 === val2) return 0;
        if (val1 === null || val1 === undefined) return 1;
        if (val2 === null || val2 === undefined) return -1;
        return val1 > val2 ? 1 : -1;
      }, args.sortAsc);
      this.grid.invalidate();
      this.grid.render();
    });

    this.resizeObserver = new ResizeObserver(() => {
      // this.grid.resizeCanvas();
      this.resizeGrid();
    });
    this.resizeObserver.observe(this.elementTarget);
  }

  resizeGrid() {
    if (!this.resizer) return;
    const rowHeight = 35;
    const headerHeight = 35; // Approximate header height
    const rowCount = this.dataView.getLength();
    let contentHeight = (rowHeight * rowCount) + headerHeight + 10;

    // Calculate available height to the bottom of the window to fill space
    const rect = this.elementTarget.getBoundingClientRect();
    const windowHeight = window.innerHeight;
    const footerPadding = 120; // Space for pagination and bottom padding
    const availableHeight = windowHeight - rect.top - footerPadding;

    let totalHeight = Math.max(contentHeight, availableHeight, 300);

    this.resizer.resizeGrid(0, {height: totalHeight, minHeight: totalHeight});
  }

  getColumns() {
    let columns = [];
    let visibleHeaders = this.headersValue.filter(h => !h.hide && !this.hiddenColumns?.has(h.field));

    visibleHeaders.forEach((h, index) => {
      let col = {
        id: h.colId || h.field,
        field: h.field,
        name: this.getHeaderNameWithIcon(h),
        sortable: true,
        minWidth: h.minWidth || 150
      };

      let dataType = h.dataType || '';
      let isFirstColumn = (index === 0);

      col.formatter = (row, cell, value, columnDef, dataContext) => {
        let formattedValue = value !== undefined && value !== null ? value : "";

        if (dataType.includes('Date') && formattedValue) {
          formattedValue = new Date(formattedValue).toLocaleDateString('en-in', {
            day: "numeric",
            year: "numeric",
            month: "short"
          });
        }

        if (isFirstColumn && this.showLinkValue) {
          return this.linkFormatter(row, cell, formattedValue, columnDef, dataContext);
        }

        return formattedValue;
      };

      if (dataType.includes('Number') || dataType.includes('Float') || dataType.includes('Integer')) {
        col.cssClass = 'text-right';
        col.headerCssClass = 'slick-header-column-right';
      }

      if (index < visibleHeaders.length - 1 && !this.element.classList.contains('lz-report-worksheet')) {
        const borderClass = 'border-r border-gray-300 border-solid';
        col.cssClass = col.cssClass ? `${col.cssClass} ${borderClass}` : borderClass;
        col.headerCssClass = col.headerCssClass ? `${col.headerCssClass} ${borderClass}` : borderClass;
      }

      columns.push(col);
    });
    return columns;
  }

  linkFormatter(row, cell, value, columnDef, dataContext) {
    let urlBase = this.urlValue || "";
    if (urlBase && dataContext[this.rowIdFieldValue]) {
      let baseUrl = urlBase.split('?')[0];
      let editUrl = `${baseUrl}/${dataContext[this.rowIdFieldValue]}/edit`;
      let displayValue = value ? value : '<span class="text-blue-500 italic">[Blank]</span>';
      return `<a href="${editUrl}" data-turbo-stream="get" class="text-blue-500 hover:underline">${displayValue}</a>`;
    }
    return value || "";
  }

  getFilterUrl(columnName) {
    if (!this.urlValue) return "#";
    let url = this.urlValue.split('?')[0];
    if (this.filterUrlValue) {
      url = this.filterUrlValue;
    } else if (url.endsWith('/rows')) {
      url = url.replace(/\/rows$/, '/filters/new');
    }

    // Use contextValue as the base
    const currentParams = new URLSearchParams(this.contextValue);

    // Reconstruct params from current filtersValue using explicit indices
    this.filtersValue.forEach((f, i) => {
      currentParams.append(`filters[${i}][column_name]`, f.original_field);
      currentParams.append(`filters[${i}][operator]`, f.operator);

      // Value is always an array in backend representation
      if (f.value && Array.isArray(f.value)) {
        f.value.forEach(v => {
          currentParams.append(`filters[${i}][value][]`, v);
        });
      }
    });

    currentParams.set('column_name', columnName);
    return `${url}?${currentParams.toString()}`;
  }

  getHeaderNameWithIcon(h) {
    let iconName = 'Type';
    let dataType = h.dataType || '';

    if (dataType) {
      if (dataType.includes('Date')) iconName = 'Calendar';
      else if (dataType.includes('Number') || dataType.includes('Float') || dataType.includes('Integer')) iconName = 'Hash';
      else if (dataType.includes('Array')) iconName = 'Layers';
      else if (dataType.includes('Ref') || h.field.includes('_display')) iconName = 'Database';
      else if (dataType.includes('String') || dataType.includes('Text')) iconName = 'Type';
    }

    const kebabMap = {
      Calendar: 'calendar-1',
      Hash: 'hash',
      FileText: 'file-text',
      Layers: 'layers',
      Database: 'database',
      Type: 'type'
    };

    const filterUrl = this.getFilterUrl(h.field);

    const hasFilter = this.filtersValue.some(f => f.original_field === h.field);
    const filterIconClass = hasFilter ? 'text-brand fill-brand' : 'text-ink-faint';

    let filterHtml = '';
    if (this.showFiltersValue) {
      filterHtml = `<a href="${filterUrl}" data-turbo-stream="get" class="flex items-center justify-center h-full px-2 border-l border-gray-200 hover:bg-gray-100 transition-colors ml-auto">
                      <i data-lucide="filter" class="w-3 h-3 ${filterIconClass}"></i>
                    </a>`;
    }

    return `<div class="flex items-center w-full h-full text-sm group">
              <div class="flex items-center px-2 flex-grow min-w-0">
                <i data-lucide="${kebabMap[iconName]}" class="mr-1.5 flex-shrink-0 w-3 h-3"></i>
                <span class="truncate" title="${h.headerName}">${h.headerName}</span>
              </div>
              ${filterHtml}
            </div>`;
  }

  renderHeaderIcons() {
    setTimeout(() => {
      createIcons({
        icons: {Type, Calendar1, Hash, FileText, Layers, Database, Filter},
        root: this.elementTarget,
        attrs: {width: 14, height: 14}
      });
    }, 100);
  }

  createTargetConnected(element) {
    if (!this.dataView || !this.grid) return;
    let newRow = JSON.parse(element.dataset.rowValue)
    this.dataView.insertItem(0, newRow);
    this.totalCountValue++;
    this.grid.updateRowCount();
    this.grid.invalidateAllRows();
    this.grid.render();
    this.grid.scrollRowIntoView(0);
    this.resizeGrid();
  }

  updateTargetConnected(element) {
    if (!this.dataView || !this.grid) return;
    let row = JSON.parse(element.dataset.rowValue)
    const id = row[this.rowIdFieldValue];
    if (id) {
      this.dataView.updateItem(id, row);
      this.grid.invalidateAllRows();
      this.grid.render();
    }
  }

  deleteTargetConnected(element) {
    if (!this.dataView) return;
    let rowId = element.dataset.rowValue.toString()
    this.dataView.deleteItem(rowId);
    this.totalCountValue--;
    this.resizeGrid();
  }

  updateCellsTargetConnected(element) {
    let parentElement = this.element.querySelector('[data-id="update-modal"]')
    if (parentElement) {
      let modalBg = parentElement.querySelector('[data-modal-target="modalBg"]');
      let modal = parentElement.querySelector('[data-modal-target="modal"]');
      if (modalBg) modalBg.classList.add('hidden')
      if (modal) modal.classList.add('hidden')
    }

    if (!this.dataView) return;

    let rows = JSON.parse(element.dataset.rowValue)
    this.dataView.beginUpdate();
    rows.forEach(row => {
      let id = row[this.rowIdFieldValue];
      let existing = this.dataView.getItemById(id);
      if (existing) {
        let updated = {...existing, ...row};
        this.dataView.updateItem(id, updated);
      }
    });
    this.dataView.endUpdate();
    this.grid.setSelectedRows([]);
  }

  updateSelected(event) {
    const selectedRows = this.grid.getSelectedRows();
    const selectedIds = selectedRows.map(idx => this.dataView.getItem(idx)[this.rowIdFieldValue]);

    let parentElement = document.querySelector('[data-id="update-modal"]') || document.body
    let form = parentElement.querySelector("form")
    if (!form) return;

    const textInputs = form.querySelectorAll('[data-type="text"],select')
    textInputs.forEach(input => {
      if (input.tagName.toLowerCase() === 'input') input.value = ''
    })

    if (selectedIds.length > 0) {
      if (selectedIds.length === 1) {
        let item = this.dataView.getItemById(selectedIds[0]);
        textInputs.forEach(input => {
          if (input.tagName.toLowerCase() === 'input') {
            if (input.dataset.sub === 'date') {
              const selectedValue = item[input.dataset.id];
              if (selectedValue) {
                const dateInstance = flatpickr(input, {dateFormat: 'd M Y'});
                dateInstance.setDate(selectedValue);
              }
            } else {
              input.value = item[input.dataset.id] || "";
            }
          } else if (input.tagName.toLowerCase() === 'select') {
            // TomSelect logic
          }
        })
        let warning = parentElement.querySelector('[data-id="row-warning"]')
        if (warning) warning.classList.add('hidden')
      } else {
        let warning = parentElement.querySelector('[data-id="row-warning"]')
        if (warning) warning.classList.remove('hidden')
      }

      const actionUrl = new URL(form.action)
      actionUrl.searchParams.set('ledger_entry_source_ids', selectedIds)
      form.action = actionUrl.toString()
      let modalBg = parentElement.querySelector('[data-modal-target="modalBg"]')
      let modal = parentElement.querySelector('[data-modal-target="modal"]')
      if (modalBg) modalBg.classList.remove('hidden')
      if (modal) modal.classList.remove('hidden')
    } else {
      alert('Please select any rows');
    }
  }

  deleteSelected(event) {
    event.preventDefault();
    const selectedRows = this.grid.getSelectedRows();
    const selectedIds = selectedRows.map(idx => this.dataView.getItem(idx)[this.rowIdFieldValue]);

    if (selectedIds.length > 0) {
      if (window.confirm("Are you sure?")) {
        let url = event.target.dataset.url;
        let token = document.querySelector('meta[name="csrf-token"]').content;
        fetch(url, {
          method: 'POST',
          headers: {'X-CSRF-Token': token, 'Content-Type': 'application/json'},
          body: JSON.stringify({row_ids: selectedIds})
        }).then(r => {
          if (r.ok) {
            this.dataView.beginUpdate();
            selectedIds.forEach(id => this.dataView.deleteItem(id));
            this.dataView.endUpdate();
            this.grid.setSelectedRows([]);
            this.grid.invalidate();
            this.totalCountValue -= selectedIds.length;
            this.resizeGrid();
          }
        });
      }
    } else {
      alert('Select rows');
    }
  }


  updateGridColumns() {
    this.grid.setColumns(this.getColumns());
    this.grid.invalidate();
    this.grid.render();
  }

  disconnect() {
    if (this.resizeObserver) this.resizeObserver.disconnect();
    if (this.grid) this.grid.destroy();
  }
}
